import { useCallback, useContext, useMemo, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import { ReactComponent as Check } from "../../../../../../public/images/user/Circle_Check.svg";
import { ReactComponent as InfoIcon } from "../../../../../../public/icons/InfoIcon.svg";
import { ReactComponent as CaretCircleRight } from "../../../../../../public/icons/CaretCircleRight.svg";
import { ReactComponent as CloseSM } from "../../../../../../public/icons/CloseSM.svg";
import {
    CommitmentRequirementDetails,
    Document,
    DocumentToUploadType,
    DossierStatusEnum,
    RolesEnum,
    Session,
} from "../../../../../../types";
import ReactSvgIcon from "../../../../../UIElements/ReactSvgIcon";
import UploadButton from "../../../../../UIElements/UploadButton";
import CustomChip from "../../../../../UIElements/CustomChip";
import {
    AcceptedFileFormatList,
    dossierStorageKey,
    sessionStorageKey,
    megaByteToByte,
    megaOctetThreshold,
} from "../../../../../../consts";
import { CustomModalContext } from "../../../../../../contexts";
import InstructionDocumentView from "./DocumentViews/InstructionDocumentView";
import { useLocalStorage } from "../../../../../../hooks";
import {
    filterCompliantDocuments,
    filterCompliantOrNotProcessedDocuments,
    filterNotCompliantDocuments,
    isInstructedDocuments,
} from "../../../utils";
import {
    deleteDocument,
    getLibraryDocument,
    updateDocument,
    updateDossier,
    uploadDocument,
} from "../../../../../../services";
import {
    _download,
    _downloadLibraryDocument,
    cloneObject,
} from "../../../../../../helpers";

import { ReactComponent as DownloadIcon } from "../../../../../../public/icons/DownloadIcon.svg";
import { DossierInfosType } from "../../../Dossier";
// import Textarea from "../../../../../UIElements/Textarea";
import { getDossierStatusByCode } from "../../../..//utils";
import ErrorModalContent from "./ErrorModalContent";
import { sortDescending } from "@/src/components/utils";

type CommitmentRequirementProps = {
    commitmentRequirement: CommitmentRequirementDetails;
    updateCommitementDetails: Function;
    dossierInfos: DossierInfosType;
    customkey: number;
};

export default function CommitmentRequirement({
    commitmentRequirement,
    updateCommitementDetails,
    dossierInfos,
    customkey,
}: CommitmentRequirementProps) {
    const { storedValue: session } =
        useLocalStorage<Session>(sessionStorageKey);

    const {
        auth: { user: currentUser, currentUserRoleId: userRoleId },
        [dossierStorageKey]: { dossierStatus },
    } = session;

    const { updateModal, updateIsCustomModalActive } =
        useContext(CustomModalContext);

    const [inputKey, setInputKey] = useState(Date.now());

    let { id: comReqId, documentQty, libraryDocument } = commitmentRequirement;

    const documents = cloneObject(
        commitmentRequirement.documents.sort(
            (cPrev, cNext) => (cNext.id || 0) - (cPrev.id || 0)
        )
    );

    const {
        id: dossierId,
        currentDossierStatusId,
        currentAssignedUserId,
    } = dossierInfos;

    const compliantDocs = filterCompliantDocuments(documents);
    const isReqDocCompleted = compliantDocs.length === documentQty;

    const isFileTypeAllowed = (fileName: string) => {
        const extension = fileName.slice(
            ((fileName.lastIndexOf(".") - 1) >>> 0) + 2
        );
        return AcceptedFileFormatList.includes("." + extension.toLowerCase());
    };

    const fileDoesNotExceed = (fileSize: number) => {
        return fileSize / megaByteToByte <= megaOctetThreshold;
    };

    const _uploadDocument = async (event: any) => {
        const file = event.target.files[0];
        if (!file) {
            return;
        }

        //console.log("file", file);

        if (file && !isFileTypeAllowed(file.name)) {
            updateModal({
                content: (
                    <ErrorModalContent
                        text={`Votre fichier n'est pas au format attendu ( ${AcceptedFileFormatList} ) `}
                        onEnd={() => {
                            updateIsCustomModalActive(false);
                        }}
                    />
                ),
            });
            updateIsCustomModalActive(true);
            //setDocHasError(!docHasError);
            return;
        }

        if (file && !fileDoesNotExceed(file.size)) {
            updateModal({
                content: (
                    <ErrorModalContent
                        text={`Votre fichier dépasse les 25Mo autorisés`}
                        onEnd={() => {
                            updateIsCustomModalActive(false);
                        }}
                    />
                ),
            });
            updateIsCustomModalActive(true);
            //setDocHasError(!docHasError);
            return;
        }

        // upload return this
        if (dossierInfos && file && documentQty >= documents.length) {
            let documentId;
            let compliant: null | true | false = null;
            let comment = null;
            if (documentQty === documents.length) {
                let concernedDocument = documents[documents.length - 1];

                const notCompliantDocs = filterNotCompliantDocuments(documents);
                const readOnlyDoc = notCompliantDocs.filter(
                    (d) => d.compliant !== true
                ).length;

                if (readOnlyDoc) {
                    const concernedDocumentWhenReadOnly = notCompliantDocs.find(
                        (d) => d.compliant !== null
                    );
                    if (concernedDocumentWhenReadOnly) {
                        concernedDocument = concernedDocumentWhenReadOnly;
                        comment = concernedDocument.comment;
                        compliant = null;
                    }
                }
                if (concernedDocument.compliant !== true) {
                    documentId = concernedDocument.id;
                }
            }

            const pyloadDocToUpload: DocumentToUploadType = {
                id: documentId,
                year_id: dossierInfos.year,
                name: file.name,
                file: file,
                commitment_requirement_id: comReqId,
                dossier_id: dossierInfos.id,
                establishment_type_id: commitmentRequirement.establishmentType,
                comment,
                type: "Report",
                document_id: documentId,
                compliant: compliant,
            };
            await uploadDocument(pyloadDocToUpload);
            await updateCommitementDetails();
        }
    };

    const extractDocumentName = (url: string): string => {
        const indexLastSlash = url.lastIndexOf("/");
        const fileName = url.substring(indexLastSlash + 1, url.length);
        return fileName;
    };

    const _downloadCommitementReqInfosDoc = async (libraryDocId: number) => {
        const libraryDocFromDb = await getLibraryDocument(libraryDocId);
        if (libraryDocFromDb) {
            await _downloadLibraryDocument(
                libraryDocId,
                extractDocumentName(libraryDocFromDb.url)
            );
        }
    };

    const download = async (documentId: number, documentName: string) => {
        await _download(documentId, documentName);
    };

    const _deleteDocument = async (documentId: number) => {
        await deleteDocument(documentId);
        await updateCommitementDetails();
        setInputKey(Date.now());
    };

    const getComleshedDocs = (documents: Document[]) => {
        switch (userRoleId) {
            case RolesEnum.ADMIN_RESEAU:
            case RolesEnum.ADMIN_REGION:
            case RolesEnum.CLIENT:
                return filterCompliantOrNotProcessedDocuments(documents).length;
            case RolesEnum.INSTRUCTEUR:
                return isInstructedDocuments(documents).length;
            case RolesEnum.ADMIN:
            case RolesEnum.SYNERPA:
                return isInstructedDocuments(documents).length;
            default:
                return 0;
        }
    };

    const isComleshedComReq = useMemo(() => {
        return getComleshedDocs(documents) === documentQty;
    }, [documents]);

    const inBeingCompleted = getDossierStatusByCode(
        DossierStatusEnum.CREATION,
        dossierStatus
    );

    // ################# instructor

    const inInstructionProgressStatus = getDossierStatusByCode(
        DossierStatusEnum.EN_COURS_D_INSTRUCTION,
        dossierStatus
    );

    // const waitingForDecisionStatus = getDossierStatusByCode(
    //     DossierStatusEnum.ATTENTE_DECISION,
    //     dossierStatus
    // );

    // const doneStatus = getDossierStatusByCode(
    //     DossierStatusEnum.FINALISE,
    //     dossierStatus
    // );

    // const sendendToInstruction = getDossierStatusByCode(
    //     DossierStatusEnum.SOUMIS,
    //     dossierStatus
    // );

    const assignedDossierStatus = getDossierStatusByCode(
        DossierStatusEnum.AFFECTE,
        dossierStatus
    );

    const _updateDossierStatus = useCallback(
        async (dossierStatus: number) => {
            if (dossierId) {
                const updatingDossierRes = await updateDossier(dossierId, {
                    dossierStatus,
                });
                if (!updatingDossierRes.detail) {
                    // handle when updating dossier status
                }
            }
        },
        [dossierId]
    );

    const makeDossierIninstructionProgress = useCallback(async () => {
        if (inInstructionProgressStatus?.id) {
            if (currentDossierStatusId !== inInstructionProgressStatus?.id) {
                await _updateDossierStatus(inInstructionProgressStatus?.id);
            }
        }
    }, [dossierId]);

    const makeDocumentNotCompliant = async (doc: Document, comment: string) => {
        if (!comment) {
            return;
        }

        const updatingDocRes = await updateDocument(doc.id, {
            //...doc,
            compliant: false,
            comment,
        });

        if (!updatingDocRes.detail) {
            await updateCommitementDetails();
            await makeDossierIninstructionProgress();
        }
    };

    const makeDocumentCompliant = async (doc: Document, value: boolean) => {
        const updatingDocRes = await updateDocument(doc.id, {
            //...doc,
            comment: null,
            compliant: value,
        });

        if (!updatingDocRes.detail) {
            await updateCommitementDetails();
            await makeDossierIninstructionProgress();
        }
    };

    // #######################jnn

    const isReadOnlyDoc = (d: Document) => {
        if (
            [
                RolesEnum.ADMIN_REGION,
                RolesEnum.ADMIN_RESEAU,
                RolesEnum.CLIENT,
            ].includes(userRoleId)
        ) {
            return d.compliant !== null;
        }
        if ([RolesEnum.INSTRUCTEUR, RolesEnum.ADMIN].includes(userRoleId)) {
            const notAllowedToEdit = ![
                assignedDossierStatus?.id,
                inInstructionProgressStatus?.id,
            ].includes(currentDossierStatusId);

            if (RolesEnum.ADMIN === userRoleId) {
                if (currentAssignedUserId) {
                    return (
                        currentAssignedUserId !== currentUser.id ||
                        notAllowedToEdit
                    );
                }
            }
            return notAllowedToEdit;
        }

        if (userRoleId === RolesEnum.SYNERPA) {
            return true;
        }

        return false;
    };

    const showUploadBtn =
        [inBeingCompleted?.id].includes(currentDossierStatusId) &&
        [
            RolesEnum.CLIENT,
            RolesEnum.ADMIN_RESEAU,
            RolesEnum.ADMIN_REGION,
        ].includes(userRoleId) &&
        // !isResendToInstruct &&
        !isReqDocCompleted;

    const renderDocumentView = (d: Document, index: number) => {
        const readOnlyDoc = isReadOnlyDoc(d);

        if (
            [
                RolesEnum.CLIENT,
                RolesEnum.ADMIN_RESEAU,
                RolesEnum.ADMIN_REGION,
            ].includes(userRoleId)
        ) {
            return (
                <Box
                    sx={{
                        display: "flex",
                        // width: "452px",
                        flexDirection: "column",
                        // justifyContent: "center",
                        // alignItems: "flex-start",
                        gap: "0.5rem",
                        "@media (max-width:1024px)": {
                            width: "100%",
                        },
                    }}
                    key={d.id}
                >
                    {!readOnlyDoc ? (
                        <>
                            <CustomChip
                                sx={{
                                    display: "flex",
                                    height: "2.5rem",
                                    padding: "1.5rem",
                                    // alignItems: "center",
                                    gap: "2rem",
                                    alignSelf: "stretch",
                                    borderRadius: "1rem",
                                    border: "2px solid #E3E6E8",
                                    background: "#FFF",

                                    ".MuiChip-label": {
                                        color: "#023",
                                        leadingTrim: "both",

                                        textEdge: "cap",
                                        fontFamily: "Roboto",
                                        fontSize: "0.875rem",
                                        fontStyle: "normal",
                                        fontWeight: "400",
                                        lineHeight: "normal",
                                    },
                                }}
                                label={d.name}
                                onDelete={async () => {
                                    await _deleteDocument(d.id);
                                }}
                                deleteIcon={
                                    showUploadBtn ? (
                                        <ReactSvgIcon
                                            ReactIcon={CloseSM}
                                            reactIconProps={{
                                                width: "24px",
                                                height: "24px",
                                            }}
                                        />
                                    ) : (
                                        <></>
                                    )
                                }
                                icon={
                                    <ReactSvgIcon
                                        sx={{
                                            cursor: "pointer",
                                        }}
                                        onClick={async () => {
                                            await download(d.id, d.name);
                                        }}
                                        ReactIcon={DownloadIcon}
                                        reactIconProps={{
                                            width: "1.5rem",
                                            height: "1.5rem",
                                        }}
                                    />
                                }
                            />
                            {/* {d?.comment ? (
                                <Textarea
                                    disabled={true}
                                    defaultValue={d?.comment ?? ""}
                                />
                            ) : null} */}
                        </>
                    ) : (
                        <InstructionDocumentView
                            key={d.id}
                            document={d}
                            isReadOnly={readOnlyDoc}
                            downloadDocument={async () => {
                                await download(d.id, d.name);
                            }}
                        />
                    )}
                    {/* {readOnlyDoc  } */}
                </Box>
            );
        }

        if (
            [
                RolesEnum.INSTRUCTEUR,
                RolesEnum.ADMIN,
                RolesEnum.SYNERPA,
            ].includes(userRoleId)
        ) {
            return (
                <InstructionDocumentView
                    key={d.id}
                    document={d}
                    makeDocumentCompliant={makeDocumentCompliant}
                    makeDocumentNotCompliant={makeDocumentNotCompliant}
                    isReadOnly={readOnlyDoc}
                    downloadDocument={async () => {
                        await download(d.id, d.name);
                    }}
                />
            );
        }

        return <></>;
    };

    const docLabelByUserRole = () => {
        switch (userRoleId) {
            case RolesEnum.ADMIN_RESEAU:
            case RolesEnum.ADMIN_REGION:
            case RolesEnum.CLIENT:
                return "importé(s)";
            case RolesEnum.INSTRUCTEUR:
                return "à instruire";
            default:
                return "";
        }
    };

    const handleFileChange = (event: any) => {
        const file = event.target.files[0];
        if (file) {
            console.log("Fichier sélectionné :", file.name);
        }
    };

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "flex-start",
                gap: "1.5rem",
                width: "100%",
            }}
            key={customkey}
        >
            <Box
                sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "1rem",
                    alignSelf: "stretch",
                    width: "100%",
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: "1rem",
                        fex: "1 0 0",
                    }}
                >
                    <ReactSvgIcon
                        // width="20px"
                        // height="20px"
                        ReactIcon={isComleshedComReq ? Check : CaretCircleRight}
                        reactIconProps={{
                            width: "26px",
                            height: "26px",
                        }}
                    />
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            alignItems: "flex-start",
                            gap: "0.5rem",
                            fex: "1 0 0",
                        }}
                    >
                        <Typography
                            sx={{
                                color: "#005499",
                                leadingTrim: "both",
                                textEdge: "cap",
                                fontFamily: "Roboto",
                                fontSize: "1.125rem",
                                fontStyle: "normal",
                                fontWeight: 500,
                                lineHeight: "1.25rem",
                                alignSelf: "stretch",
                            }}
                        >
                            {commitmentRequirement.name}
                        </Typography>
                        <Typography
                            sx={{
                                color: "#023",
                                leadingTrim: "both",
                                textEdge: "cap",
                                fontFamily: "Roboto",
                                fontSize: "1rem",
                                fontStyle: "normal",
                                fontWeight: 400,
                                lineHeight: "1.25rem",
                                alignSelf: "stretch",
                            }}
                        >
                            {commitmentRequirement.documentaryProof}
                        </Typography>
                    </Box>
                </Box>
                {libraryDocument ? (
                    <ReactSvgIcon
                        width="20px"
                        height="20px"
                        onClick={async () => {
                            if (libraryDocument) {
                                await _downloadCommitementReqInfosDoc(
                                    libraryDocument
                                );
                            }
                        }}
                        ReactIcon={InfoIcon}
                        sx={{ float: "left", cursor: "pointer" }}
                    />
                ) : null}
            </Box>

            <Box
                sx={{
                    display: "flex",
                    width: "60.5rem",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "flex-start",
                    gap: "1rem",
                }}
            >
                <Typography
                    sx={{
                        color: "#023",

                        fontFamily: "Roboto",
                        fontSize: "1rem",
                        fontStyle: "normal",
                        fontWeight: 500,
                        lineHeight: "normal",
                    }}
                >
                    {`Document(s) ${docLabelByUserRole()} (${getComleshedDocs(
                        documents
                    )}/${documentQty})`}
                </Typography>
            </Box>

            {showUploadBtn ? (
                <UploadButton
                    label="Ajouter un document"
                    key={inputKey}
                    inputprops={{
                        onChange: _uploadDocument,
                        inputProps: {
                            accept: AcceptedFileFormatList,
                            id: inputKey + "",
                            key: inputKey,
                            //multiple: commitmentRequirement.documentQty > 1,
                            //maxFiles: commitmentRequirement.documentQty,
                        },
                    }}
                />
            ) : null}

            {documents.length
                ? documents.map((d: Document, index: number) => {
                      return renderDocumentView(d, index);
                  })
                : null}

            <Box
                sx={{
                    height: "0.125rem",
                    alignSelf: "stretch",
                    background: "#E3E6E8",
                }}
            ></Box>
        </Box>
    );
}
